import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PostAdd as PostAddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Insights as InsightsIcon,
  RateReview as RateReviewIcon,
  Settings as SettingsIcon,
  CloudQueue as CloudQueueIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import {
  getNavRail,
  isNavGroupActive,
  NAV_FLYOUT_WIDTH,
  parseNavPath,
} from '../config/navGroups';

const ICONS = {
  Dashboard: DashboardIcon,
  PostAdd: PostAddIcon,
  Insights: InsightsIcon,
  RateReview: RateReviewIcon,
  AutoAwesome: AutoAwesomeIcon,
  CloudQueue: CloudQueueIcon,
  Settings: SettingsIcon,
};

const HOVER_OPEN_MS = 150;
const HOVER_CLOSE_MS = 120;

function NavIcon({ name, active }) {
  const Icon = ICONS[name] || DashboardIcon;
  return (
    <Icon
      sx={{
        color: active ? '#58a6ff' : '#8b949e',
        fontSize: '1.35rem',
      }}
    />
  );
}

function rowButtonSx(active) {
  return {
    borderRadius: 2,
    mb: 0.5,
    py: 1.25,
    px: 1.5,
    backgroundColor: active ? 'rgba(88, 166, 255, 0.12)' : 'transparent',
    border: active ? '1px solid #58a6ff' : '1px solid transparent',
    '&:hover': {
      backgroundColor: active ? 'rgba(88, 166, 255, 0.16)' : 'rgba(88, 166, 255, 0.08)',
      borderColor: '#58a6ff',
    },
  };
}

function FlyoutLink({ link, loc, onNavigate, nested }) {
  const active = link.match(loc);
  return (
    <ListItemButton
      onClick={() => onNavigate(link.path)}
      sx={{
        ...rowButtonSx(active),
        pl: nested ? 2.5 : 1.5,
        py: 1,
      }}
    >
      <ListItemText
        primary={link.label}
        primaryTypographyProps={{
          fontSize: '0.875rem',
          fontWeight: active ? 700 : 500,
          color: active ? '#f0f6fc' : '#c9d1d9',
        }}
      />
    </ListItemButton>
  );
}

function FlyoutPanel({ group, loc, anchorEl, open, onNavigate, onMouseEnter, onMouseLeave }) {
  if (!group?.children?.length) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={[
        { name: 'offset', options: { offset: [4, 0] } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
    >
      <Paper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        elevation={8}
        sx={{
          width: NAV_FLYOUT_WIDTH,
          ml: 0.5,
          p: 1,
          bgcolor: '#161b22',
          border: '1px solid rgba(48, 54, 61, 0.8)',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <Typography
          variant="overline"
          sx={{ px: 1.5, py: 0.5, color: '#8b949e', display: 'block', letterSpacing: '0.08em' }}
        >
          {group.label}
        </Typography>
        <List dense disablePadding>
          {group.children.map((entry, idx) => {
            if (entry.type === 'section') {
              return (
                <Box key={entry.label}>
                  {idx > 0 && <Divider sx={{ my: 0.75, borderColor: 'rgba(48, 54, 61, 0.6)' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      px: 1.5,
                      pt: 0.5,
                      pb: 0.25,
                      color: '#8b949e',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {entry.label}
                  </Typography>
                  {entry.children.map((link) => (
                    <FlyoutLink
                      key={link.path}
                      link={link}
                      loc={loc}
                      nested
                      onNavigate={onNavigate}
                    />
                  ))}
                </Box>
              );
            }
            return (
              <FlyoutLink
                key={entry.path}
                link={entry}
                loc={loc}
                onNavigate={onNavigate}
              />
            );
          })}
        </List>
      </Paper>
    </Popper>
  );
}

function collapsedRowSx(active, collapsed) {
  return {
    ...rowButtonSx(active),
    justifyContent: collapsed ? 'center' : 'flex-start',
    px: collapsed ? 1 : 1.5,
  };
}

function DesktopNav({ onNavigate, collapsed = false }) {
  const location = useLocation();
  const loc = { pathname: location.pathname, search: location.search };
  const navRail = getNavRail();
  const [openGroupId, setOpenGroupId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const openFlyout = (groupId, el) => {
    clearTimers();
    setOpenGroupId(groupId);
    setAnchorEl(el);
  };

  const scheduleOpen = (groupId, el) => {
    clearTimers();
    openTimer.current = setTimeout(() => openFlyout(groupId, el), HOVER_OPEN_MS);
  };

  const scheduleClose = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => {
      setOpenGroupId(null);
      setAnchorEl(null);
    }, HOVER_CLOSE_MS);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const handleNavigate = (path) => {
    onNavigate(path);
    setOpenGroupId(null);
    setAnchorEl(null);
  };

  const flyoutGroup = navRail.find((g) => g.id === openGroupId);

  return (
    <List sx={{ py: 0.5 }}>
      {navRail.map((item) => {
        const active = isNavGroupActive(item, loc);
        const hasFlyout = Boolean(!item.directPath && item.children?.length);

        if (!hasFlyout) {
          const button = (
            <ListItemButton
              onClick={() => onNavigate(item.directPath)}
              sx={collapsedRowSx(active, collapsed)}
            >
              <ListItemIcon
                sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}
              >
                <NavIcon name={item.icon} active={active} />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 700 : 600,
                    color: active ? '#f0f6fc' : '#c9d1d9',
                  }}
                />
              )}
            </ListItemButton>
          );
          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              {collapsed ? (
                <Tooltip title={item.label} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );
        }

        const groupActive = active || openGroupId === item.id;

        return (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onMouseEnter={(e) => scheduleOpen(item.id, e.currentTarget)}
              onMouseLeave={scheduleClose}
              onClick={(e) => {
                if (openGroupId === item.id) {
                  setOpenGroupId(null);
                  setAnchorEl(null);
                } else {
                  openFlyout(item.id, e.currentTarget);
                }
              }}
              sx={collapsedRowSx(groupActive, collapsed)}
            >
              <ListItemIcon
                sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}
              >
                <NavIcon name={item.icon} active={groupActive} />
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: active ? 700 : 600,
                      color: active ? '#f0f6fc' : '#c9d1d9',
                    }}
                  />
                  <ChevronRightIcon sx={{ color: '#8b949e', fontSize: '1.1rem' }} />
                </>
              )}
            </ListItemButton>
          </ListItem>
        );
      })}
      <FlyoutPanel
        group={flyoutGroup}
        loc={loc}
        anchorEl={anchorEl}
        open={Boolean(openGroupId && anchorEl && flyoutGroup)}
        onNavigate={handleNavigate}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      />
    </List>
  );
}

function MobileNavLink({ link, loc, onNavigate, nested }) {
  const active = link.match(loc);
  return (
    <ListItemButton
      onClick={() => onNavigate(link.path)}
      sx={{ ...rowButtonSx(active), pl: nested ? 4 : 2.5 }}
    >
      <ListItemText
        primary={link.label}
        primaryTypographyProps={{
          fontSize: '0.875rem',
          fontWeight: active ? 700 : 500,
          color: active ? '#f0f6fc' : '#c9d1d9',
        }}
      />
    </ListItemButton>
  );
}

function MobileNav({ onNavigate }) {
  const location = useLocation();
  const loc = { pathname: location.pathname, search: location.search };
  const navRail = getNavRail();
  const [expanded, setExpanded] = useState({});
  const [reportsOpen, setReportsOpen] = useState(false);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <List sx={{ py: 0.5 }}>
      {navRail.map((item) => {
        const active = isNavGroupActive(item, loc);

        if (item.directPath) {
          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => onNavigate(item.directPath)}
                sx={rowButtonSx(active)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <NavIcon name={item.icon} active={active} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        }

        const isOpen = expanded[item.id] ?? active;

        return (
          <Box key={item.id} sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => toggle(item.id)}
              sx={rowButtonSx(active)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <NavIcon name={item.icon} active={active} />
              </ListItemIcon>
              <ListItemText primary={item.label} />
              {isOpen ? (
                <ExpandLessIcon sx={{ color: '#8b949e' }} />
              ) : (
                <ExpandMoreIcon sx={{ color: '#8b949e' }} />
              )}
            </ListItemButton>
            <Collapse in={isOpen}>
              <List disablePadding>
                {item.children.map((entry) => {
                  if (entry.type === 'section') {
                    const sectionActive = entry.match(loc);
                    const sectionExpanded = reportsOpen || sectionActive;
                    return (
                      <Box key={entry.label}>
                        <ListItemButton
                          onClick={() => setReportsOpen(!sectionExpanded)}
                          sx={{ pl: 3, py: 0.75 }}
                        >
                          <ListItemText
                            primary={entry.label}
                            primaryTypographyProps={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#8b949e',
                              textTransform: 'uppercase',
                            }}
                          />
                          {sectionExpanded ? (
                            <ExpandLessIcon sx={{ color: '#8b949e', fontSize: '1rem' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ color: '#8b949e', fontSize: '1rem' }} />
                          )}
                        </ListItemButton>
                        <Collapse in={sectionExpanded}>
                          {entry.children.map((link) => (
                            <MobileNavLink
                              key={link.path}
                              link={link}
                              loc={loc}
                              nested
                              onNavigate={onNavigate}
                            />
                          ))}
                        </Collapse>
                      </Box>
                    );
                  }
                  return (
                    <MobileNavLink
                      key={entry.path}
                      link={entry}
                      loc={loc}
                      onNavigate={onNavigate}
                    />
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
}

export default function NavSidebar({ isMobile, onNavigate, collapsed = false }) {
  const handleNavigate = useCallback(
    (path) => {
      const { pathname, search } = parseNavPath(path);
      onNavigate(pathname + search);
    },
    [onNavigate]
  );

  if (isMobile) {
    return <MobileNav onNavigate={handleNavigate} />;
  }
  return <DesktopNav onNavigate={handleNavigate} collapsed={collapsed} />;
}
